import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { X, Mail, Send, FileText, Clock } from 'lucide-react';
import { getEmailTemplates, processTemplate, sendEmail } from '../services/emailService';
import { detectarDatosFaltantes, generarEmailPersonalizado } from '../utils/emailPersonalizado';

const EnviarEmail = ({ startup, reporte, tipo, onEnviar, onCancelar }) => {
  const [asunto, setAsunto] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    // Cargar plantillas de forma asíncrona
    const cargarPlantillas = async () => {
      try {
        const templates = await getEmailTemplates();
        
        // Verificar si ya hay datos y es un recordatorio
        const tieneDatos = startup.datos && (
          (startup.datos.kpis && Object.keys(startup.datos.kpis).some(k => {
            const valor = startup.datos.kpis[k];
            return valor !== null && valor !== undefined && valor !== '';
          })) || 
          (startup.datos.milestones && startup.datos.milestones.length > 0)
        );
        
        // Si es recordatorio y ya hay datos, generar email personalizado
        if ((tipo === 'recordatorio' || tipo === 'recordatorioFinal') && tieneDatos) {
          const datosFaltantes = detectarDatosFaltantes(startup);
          const cuerpoPersonalizado = generarEmailPersonalizado(startup, reporte, datosFaltantes);
          
          // Generar asunto personalizado
          const templateKey = tipo === 'recordatorioFinal' ? 'recordatorioFinal' : 'recordatorio';
          const templateBase = templates[templateKey];
          
          if (!templateBase || !templateBase.asunto) {
            console.error(`Plantilla ${templateKey} no encontrada`);
            setAsunto('Recordatorio - Datos trimestrales pendientes');
            setCuerpo(cuerpoPersonalizado);
            return;
          }
          
          const variables = {
            startupNombre: startup.nombre,
            fundadorNombre: startup.fundador,
            reporteNombre: reporte.nombre,
            arcangelNombre: startup.arcangel || ''
          };
          const asuntoPersonalizado = processTemplate(templateBase.asunto, variables);
          
          setAsunto(asuntoPersonalizado);
          setCuerpo(cuerpoPersonalizado);
        } else {
          // Usar plantilla normal
          let templateKey = 'solicitudInicial';
          
          if (tipo === 'recordatorio') {
            templateKey = 'recordatorio';
          } else if (tipo === 'recordatorioFinal') {
            templateKey = 'recordatorioFinal';
          }

          const template = templates[templateKey];
          
          if (!template || !template.asunto || !template.cuerpo) {
            console.error(`Plantilla ${templateKey} no encontrada`);
            // Usar valores por defecto
            setAsunto(`Solicitud de datos trimestrales - ${startup.nombre} - ${reporte.nombre}`);
            setCuerpo(`Hola ${startup.fundador || 'fundador'},

Espero que te encuentres bien. Te escribo para solicitar la información trimestral de ${startup.nombre} correspondiente al ${reporte.nombre}.

Necesitamos los siguientes datos para completar el reporte trimestral del club:

**KPIs Financieros:**
- Ingresos del trimestre
- Gastos del trimestre
- Número de empleados actual
- Runway (meses de capital disponible)
- ¿Tienes una ronda de financiación abierta? (Sí/No)
- Si es así: valoración y progreso de la ronda

**Hitos Relevantes del Trimestre:**
Por favor, comparte los hitos más importantes del trimestre en las siguientes áreas:
- Comercial
- Producto
- Tecnología
- Recursos Humanos
- Financiero
- General
- Otros

Puedes enviar esta información en el formato que prefieras. Si tienes alguna pregunta, no dudes en contactarme.

Gracias por tu colaboración.

Saludos cordiales`);
            return;
          }
          
          // Procesar variables
          const variables = {
            startupNombre: startup.nombre,
            fundadorNombre: startup.fundador,
            reporteNombre: reporte.nombre,
            arcangelNombre: startup.arcangel || ''
          };

          setAsunto(processTemplate(template.asunto, variables));
          setCuerpo(processTemplate(template.cuerpo, variables));
        }
      } catch (error) {
        console.error('Error cargando plantillas:', error);
        // Usar valores por defecto en caso de error
        setAsunto(`Solicitud de datos trimestrales - ${startup.nombre} - ${reporte.nombre}`);
        setCuerpo(`Hola ${startup.fundador || 'fundador'},

Espero que te encuentres bien. Te escribo para solicitar la información trimestral de ${startup.nombre} correspondiente al ${reporte.nombre}.

Necesitamos los siguientes datos para completar el reporte trimestral del club:

**KPIs Financieros:**
- Ingresos del trimestre
- Gastos del trimestre
- Número de empleados actual
- Runway (meses de capital disponible)
- ¿Tienes una ronda de financiación abierta? (Sí/No)
- Si es así: valoración y progreso de la ronda

**Hitos Relevantes del Trimestre:**
Por favor, comparte los hitos más importantes del trimestre en las siguientes áreas:
- Comercial
- Producto
- Tecnología
- Recursos Humanos
- Financiero
- General
- Otros

Puedes enviar esta información en el formato que prefieras. Si tienes alguna pregunta, no dudes en contactarme.

Gracias por tu colaboración.

Saludos cordiales`);
      }
    };
    
    cargarPlantillas();
  }, [startup, reporte, tipo]);

  const handleEnviar = async () => {
    setEnviando(true);
    
    try {
      // Enviar email usando el servicio
      const result = await sendEmail(startup.email, asunto, cuerpo, tipo);
      
      // Mostrar mensaje según el resultado
      if (result.modo === 'fallback') {
        alert('⚠️ El email se ha guardado pero NO se ha enviado realmente.\n\nLa API no está disponible. Configura el backend para enviar emails reales.');
      } else {
        alert('✅ Email enviado exitosamente');
      }
      
      setEnviando(false);
      onEnviar();
    } catch (error) {
      console.error('Error enviando email:', error);
      alert(`Error al enviar el email: ${error.message}\n\nEl email se ha guardado en modo fallback.`);
      setEnviando(false);
      onEnviar(); // Aún así actualizar el estado
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-card border-b">
          <div>
            <CardTitle className="flex items-center gap-2">
              {tipo === 'solicitud' ? (
                <>
                  <Mail className="h-5 w-5" />
                  Enviar Solicitud de Datos
                </>
              ) : tipo === 'recordatorioFinal' ? (
                <>
                  <Clock className="h-5 w-5" />
                  Enviar Recordatorio Final
                </>
              ) : (
                <>
                  <Clock className="h-5 w-5" />
                  {startup.datos && (startup.datos.kpis || startup.datos.milestones?.length > 0)
                    ? 'Enviar Recordatorio Personalizado'
                    : 'Enviar Recordatorio'}
                </>
              )}
            </CardTitle>
            <CardDescription>
              {startup.nombre} • {startup.email}
            </CardDescription>
          </div>
          <Button variant="ghost" size="icon" onClick={onCancelar}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          <div className="p-4 rounded-lg bg-muted/30 border border-border/50">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Plantilla del Email</span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Esta es la plantilla que se enviará. Puedes editarla antes de enviar.
            </p>
            <div className="space-y-2">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Asunto:</label>
                <input
                  type="text"
                  value={asunto}
                  onChange={(e) => setAsunto(e.target.value)}
                  className="w-full p-2 rounded-md border border-input bg-background text-sm mb-3"
                />
              </div>
              <textarea
                value={cuerpo}
                onChange={(e) => setCuerpo(e.target.value)}
                className="w-full p-3 rounded-md border border-input bg-background font-mono text-sm min-h-[400px]"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-border/50">
            <Button variant="outline" onClick={onCancelar} disabled={enviando}>
              Cancelar
            </Button>
            <Button onClick={handleEnviar} disabled={enviando}>
              {enviando ? (
                <>
                  <Clock className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Enviar Email
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EnviarEmail;

